import { useMemo } from 'react';

import type { RunStatus } from '@/shared/ui/RunStatusTag';
import { isValidRunStatus } from '@/shared/ui/RunStatusTag';

import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';

import { mapDeploymentStatusToRunStatus, useDeploymentsByRunIds } from '@/features/deployment';

type AssetType = 'run' | 'dataset' | 'feature_set' | 'parameter_set' | 'artifact';

/**
 * 런 자산 상세: 자산 `run.status`가 execution_succeeded일 때 배포 API로 보강한 표시용 RunStatus.
 * 서머리 카드·워크벤치 state 등 상위에서 한 번만 조회해 공유한다.
 */
export function useRunDisplayStatusWithDeployment(
  assetType: AssetType | undefined,
  data: AssetDetailResponse | undefined
): { displayStatus: RunStatus | null; isLoading: boolean } {
  const runStatus = data?.run?.status;
  const runId = data?.id;

  const shouldFetchDeployment = useMemo(() => {
    return assetType === 'run' && runId != null && runStatus === 'execution_succeeded';
  }, [assetType, runId, runStatus]);

  const { data: deploymentData, isLoading } = useDeploymentsByRunIds({
    runIds: runId ? [runId.toString()] : [],
    environment: 'dev',
    includeDeleted: false,
    enabled: shouldFetchDeployment,
  });

  const displayStatus = useMemo(() => {
    if (assetType !== 'run' || !runId || !runStatus) {
      return null;
    }

    if (runStatus !== 'execution_succeeded') {
      return isValidRunStatus(runStatus) ? runStatus : null;
    }

    if (!shouldFetchDeployment) {
      return isValidRunStatus(runStatus) ? runStatus : null;
    }

    if (isLoading && !deploymentData) {
      return null;
    }

    if (deploymentData?.results.length && deploymentData.results[0].found && deploymentData.results[0].deployment) {
      const deploymentStatus = deploymentData.results[0].deployment.status;
      return mapDeploymentStatusToRunStatus(deploymentStatus);
    }

    return 'execution_succeeded';
  }, [assetType, runId, runStatus, shouldFetchDeployment, isLoading, deploymentData]);

  return { displayStatus, isLoading };
}
